import { createContext, FC, PropsWithChildren, useEffect, useState } from "react";
import useUserToken from "../hooks/useUserToken";
import { useRefreshToken } from "../hooks/useAuthentication";
import { useIdleTimer } from "react-idle-timer";
import { useQueryClient } from "@tanstack/react-query";
import { useLogoutReason } from "../hooks/useLogoutReason";

const IDLE_TIMEOUT = 1000 * 60 * 60; // 1h

type AuthContextType = {
  token?: string | null;
  refreshToken?: string | null;
  isLoggedIn: boolean;
  setTokens: (value: { accessToken: string; refreshToken: string }) => void;
  handleLogout: () => void;
};

export const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  setTokens: () => {},
  handleLogout: () => {},
});

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const UserToken = useUserToken();
  const [_accessToken, _setAccessToken] = useState<string | null>(UserToken.getAccessToken());
  const [_refreshToken, _setRefreshToken] = useState<string | null>(UserToken.getRefreshToken());
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(UserToken.valid(_accessToken));
  const refreshToken = useRefreshToken();
  const queryClient = useQueryClient();
  const { setLogoutReason } = useLogoutReason();

  useEffect(() => {
    const getNewTokens = async () => {
      try {
        const tokens = await refreshToken.mutateAsync(_refreshToken);
        if (tokens) setTokens(tokens);
      } catch (e) {
        console.error(e);
        clearTokens();
      }
    };
    if (UserToken.valid(_refreshToken) && !UserToken.valid(_accessToken)) {
      getNewTokens();
    }
  }, []);

  const handleOnIdle = () => {
    setLogoutReason("idle");
    clearTokens();
    window.location.href = "/";
  };

  useIdleTimer({
    timeout: IDLE_TIMEOUT, // 1h
    onIdle: handleOnIdle,
    debounce: 500,
    crossTab: true,
  });

  const clearTokens = () => {
    _setAccessToken(null);
    _setRefreshToken(null);
    UserToken.clear();
    setIsLoggedIn(false);
  };

  const handleLogout = () => {
    setLogoutReason("manual");
    clearTokens();
    queryClient.removeQueries();
  };

  const setTokens = (value: { accessToken: string; refreshToken: string }) => {
    const isValid = UserToken.valid(value.accessToken);
    if (!isValid) return clearTokens();

    _setAccessToken(value.accessToken);
    _setRefreshToken(value.refreshToken);
    UserToken.setAccessToken(value.accessToken);
    UserToken.setRefreshToken(value.refreshToken);

    setIsLoggedIn(true);
  };

  return (
    <AuthContext.Provider
      value={{
        token: _accessToken,
        refreshToken: _refreshToken,
        isLoggedIn,
        handleLogout,
        setTokens,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
